import {
  setEncryptedItem,
  getEncryptedItem,
  removeEncryptedItem,
} from './encryption';

// Storage keys (same pattern as qrbuddy-web)
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'miles_widget_authkey',
  ANONYMOUS_AUTH_TOKEN: 'miles_widget_anonymousauthkey',
  CONVERSATION_ID: 'miles_widget_conversation',
  USER_DATA: 'miles_widget_user',
  DEALER_ID: 'miles_widget_dealer',
  IS_WELCOME: 'is_welcome',
  IS_SHOW_ENHANCE_VIEW: 'is_show_enhance_view',
  IS_WEB_APP_USER: 'is_web_app_user',
  USER_SESSION_ID: 'user_session_id',
} as const;

/**
 * Store authentication token
 * NOTE: Token is stored in PLAIN TEXT (not encrypted) so Feathers can read it directly
 * This matches the qrbuddy-web implementation where JWT is stored unencrypted
 */
export const setAuthToken = (token: string): void => {
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
};

/**
 * Get authentication token
 * NOTE: Token is stored in PLAIN TEXT (not encrypted)
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Store anonymous token
 * NOTE: Token is stored in PLAIN TEXT (not encrypted) so Feathers can read it directly
 * This matches the qrbuddy-web implementation where JWT is stored unencrypted
 */
export const setAnonymousToken = (token: string): void => {
  localStorage.setItem(STORAGE_KEYS.ANONYMOUS_AUTH_TOKEN, token);
};

/**
 * Get anonymous token
 * NOTE: Token is stored in PLAIN TEXT (not encrypted)
 */
export const getAnonymousToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.ANONYMOUS_AUTH_TOKEN);
};

/**
 * Store conversation ID
 */
export const setConversationId = (conversationId: string): void => {
  setEncryptedItem(STORAGE_KEYS.CONVERSATION_ID, conversationId);
};

/**
 * Get conversation ID
 */
export const getConversationId = (): string | null => {
  return getEncryptedItem(STORAGE_KEYS.CONVERSATION_ID);
};

/**
 * Store user data
 */
export const setUserData = (user: any): void => {
  setEncryptedItem(STORAGE_KEYS.USER_DATA, user);
};

/**
 * Get user data
 */
export const getUserData = (): any => {
  return getEncryptedItem(STORAGE_KEYS.USER_DATA);
};

/**
 * Store dealer ID
 */
export const setDealerId = (dealerId: string): void => {
  setEncryptedItem(STORAGE_KEYS.DEALER_ID, dealerId);
};

/**
 * Get dealer ID
 */
export const getDealerId = (): string | null => {
  return getEncryptedItem(STORAGE_KEYS.DEALER_ID);
};

export interface WidgetStorageSettings {
  authToken?: string | null;
  anonymousAuthToken?: string | null;
  conversationId?: string | null;
  userData?: any | null;
  dealerId?: string | null;
  isWelcome?: boolean | null;
  isShowEnhanceView?: boolean | null;
}

/**
 * Set multiple storage values at once.
 * Use null to remove a value, omit a key to leave it unchanged.
 */
export const setWidgetStorageSettings = (settings: WidgetStorageSettings): void => {
  if (!settings || typeof settings !== 'object') return;

  if ('authToken' in settings) {
    if (typeof settings.authToken === 'string') {
      setAuthToken(settings.authToken);
    } else if (settings.authToken === null) {
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    }
  }

  if ('anonymousAuthToken' in settings) {
    if (typeof settings.anonymousAuthToken === 'string') {
      setAnonymousToken(settings.anonymousAuthToken);
    } else if (settings.anonymousAuthToken === null) {
      localStorage.removeItem(STORAGE_KEYS.ANONYMOUS_AUTH_TOKEN);
    }
  }

  if ('conversationId' in settings) {
    if (typeof settings.conversationId === 'string') {
      setConversationId(settings.conversationId);
    } else if (settings.conversationId === null) {
      removeEncryptedItem(STORAGE_KEYS.CONVERSATION_ID);
    }
  }

  if ('userData' in settings) {
    if (settings.userData !== null && settings.userData !== undefined) {
      setUserData(settings.userData);
    } else if (settings.userData === null) {
      removeEncryptedItem(STORAGE_KEYS.USER_DATA);
    }
  }

  if ('dealerId' in settings) {
    if (typeof settings.dealerId === 'string') {
      setDealerId(settings.dealerId);
    } else if (settings.dealerId === null) {
      removeEncryptedItem(STORAGE_KEYS.DEALER_ID);
    }
  }

  if ('isWelcome' in settings) {
    if (typeof settings.isWelcome === 'boolean') {
      setIsWelcome(settings.isWelcome);
    } else if (settings.isWelcome === null) {
      removeEncryptedItem(STORAGE_KEYS.IS_WELCOME);
    }
  }
  
  if ('isShowEnhanceView' in settings) {
    console.log('settings', settings);
    if (typeof settings.isShowEnhanceView === 'boolean') {
      setIsShowEnhanceView(settings.isShowEnhanceView);
    } else if (settings.isShowEnhanceView === null) {
      removeEncryptedItem(STORAGE_KEYS.IS_SHOW_ENHANCE_VIEW);
    }
  }
  
  if ('isWebAppUser' in settings) {
    console.log('settings', settings);
    if (typeof settings.isWebAppUser === 'boolean') {
      setIsWebAppUser(settings.isWebAppUser);
    } else if (settings.isShowEnhanceView === null) {
      removeEncryptedItem(STORAGE_KEYS.IS_WEB_APP_USER);
    }
  }
};

/**
 * Clear all widget data
 */
export const clearWidgetData = (): void => {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN); // Plain text token
  localStorage.removeItem(STORAGE_KEYS.ANONYMOUS_AUTH_TOKEN); // Plain text token
  removeEncryptedItem(STORAGE_KEYS.CONVERSATION_ID);
  removeEncryptedItem(STORAGE_KEYS.USER_DATA);
  removeEncryptedItem(STORAGE_KEYS.DEALER_ID);
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = getAuthToken();
  return !!token;
};



/**
 * Store isWelcome
 */
export const setIsWelcome = (isWelcome: boolean): void => {
  setEncryptedItem(STORAGE_KEYS.IS_WELCOME, isWelcome);
};

/**
 * Get isWelcome
 */
export const getIsWelcome = (): boolean | null => {
  const data = getEncryptedItem(STORAGE_KEYS.IS_WELCOME);

  if (data) {
    return false;
  } else {
    return true;
  }
};


/**
 * Store isShowEnhanceView
 */
export const setIsShowEnhanceView = (isShowEnhanceView: boolean): void => {
  setEncryptedItem(STORAGE_KEYS.IS_SHOW_ENHANCE_VIEW, isShowEnhanceView);
};

/**
 * Get isShowEnhanceView
 */
export const getIsShowEnhanceView = (): boolean => {
  const data = getEncryptedItem(STORAGE_KEYS.IS_SHOW_ENHANCE_VIEW);

  return data ?? true;
};


/**
 * Store isWebAppUser
 */
export const setIsWebAppUser = (isWebAppUser: boolean): void => {
  setEncryptedItem(STORAGE_KEYS.IS_WEB_APP_USER, isWebAppUser);
};

/**
 * Get isWebAppUser
 */
export const getIsWebAppUser = (): boolean => {
  const data = getEncryptedItem(STORAGE_KEYS.IS_WEB_APP_USER);

  return data ?? false;
};

/**
 * Store user session ID
 */
export const setUserSessionId = (sessionId: string): void => {
  setEncryptedItem(STORAGE_KEYS.USER_SESSION_ID, sessionId);
};

/**
 * Get user session ID
 */
export const getUserSessionId = (): string | null => {
  return getEncryptedItem(STORAGE_KEYS.USER_SESSION_ID);
};
